import { useEffect, useMemo, useState } from 'react';

import { reportError } from 'helpers/error-logging';

import { fetchSkuGroups, fetchSkuSubgroups } from './SkuItems.helpers';
import { getCategoryId, getCategoryMenuId, getMenuId, isUncategorizedCategory } from './SkuMenuData.normalizers';

const useSkuCategories = (venueId, categoryIds, refreshKey) => {
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        const [groupsData, subgroupsData] = await Promise.all([fetchSkuGroups(venueId), fetchSkuSubgroups(venueId)]);

        if (!isActive) return;

        setGroups((groupsData || []).filter(group => !!getMenuId(group)));
        setSubgroups(
          (subgroupsData || []).filter(
            subgroup => !!getCategoryId(subgroup) && !!getCategoryMenuId(subgroup) && !isUncategorizedCategory(subgroup)
          )
        );
      } catch (error) {
        if (!isActive) return;
        reportError('Error loading SKU categories', error);
      } finally {
        if (isActive) setCategoriesLoading(false);
      }
    };

    loadCategories();

    return () => {
      isActive = false;
    };
  }, [venueId, refreshKey]);

  const preselectedCategories = useMemo(() => {
    if (!categoryIds?.length) {
      return [];
    }

    const idSet = new Set(categoryIds.map(String));
    return subgroups.filter(subgroup => idSet.has(String(getCategoryId(subgroup))));
  }, [categoryIds, subgroups]);

  return { groups, subgroups, categoriesLoading, preselectedCategories };
};

export default useSkuCategories;
